import React, { useEffect, useState } from 'react';
import shoppableAdVideo from '../../../../assets/creative/shoppable/video_9_16.mp4';
import shoppableAdBanner from '../../../../assets/creative/shoppable/display_9_16.png';
import posterOne from '../../../../assets/creative/shoppable/thumb_1.png';
import posterTwo from '../../../../assets/creative/shoppable/thumb_1.png';
import posterThree from '../../../../assets/creative/shoppable/thumb_1.png';

const PRODUCTS = [
    { name: 'Sport Banner', price: '$42.50', image: posterOne },
    { name: 'Travel Banner', price: '$38.90', image: posterTwo },
    { name: 'Hero Banner', price: '$45.20', image: posterThree },
];

// Placeholder caption until this comes from the product feed.
const CAPTION = 'Coca Cola';

// How many product chips fit in each layout's available space.
// Temporarily capped at 1 everywhere (Horizontal Panel, Overlay, Side Panel).
const VISIBLE_COUNT = {
    horizontalPanel: 1,
    overlay: 1,
    sidePanel: 1,
};

const ProductChip = ({ product, ctaText }) => (
    <div className="ia-product-chip">
        <img className="ia-product-chip-thumb" src={product.image} alt={product.name} />
        <div className="ia-product-chip-info">
            <div className="ia-product-chip-title-row">
                <span className="ia-product-chip-caption">{CAPTION}</span>
                <i className="fa-solid fa-up-right-from-square ia-product-chip-expand"></i>
            </div>
            <div className="ia-product-chip-cta">
                <i className="fa-solid fa-cart-shopping"></i>
                <span>{ctaText || 'Book Now'}</span>
            </div>
        </div>
    </div>
);

const ProductStrip = ({ layout, panelStyle, ctaText }) => {
    // No longer auto-cycles between products — with only one chip shown at a
    // time and the same caption/CTA either way, the interval was just forcing
    // a remount (and the fade-in animation restarting) every couple seconds,
    // which read as the whole overlay blinking.
    const visibleCount = VISIBLE_COUNT[layout] || 2;
    const items = Array.from({ length: visibleCount }, (_, i) => PRODUCTS[i % PRODUCTS.length]);

    return (
        <div className={`ia-product-strip ${panelStyle}`}>
            {items.map((p, i) => (
                <ProductChip key={`${p.name}-${i}`} product={p} ctaText={ctaText} />
            ))}
        </div>
    );
};

// The video is a single persistent DOM node across every layout/position change —
// only the surrounding container sizes change (via CSS transition), so the source
// keeps playing uninterrupted and object-fit: contain keeps its own aspect ratio
// intact as it resizes, instead of stretching or cropping.
const ShoppableAdPreview = ({ layout, position, panelStyle, format = 'olv', ctaText }) => {
    const isDisplay = format === 'display';
    // The layout actually being rendered lags behind the `layout` prop — both on
    // first load and every time layout/position changes, the video first shows
    // full-screen (revealedLayout: null, no panel shift, no product panel) for 2s,
    // then the video shift AND the panel reveal happen together — matching the
    // same behavior as the QR preview.
    const [revealedLayout, setRevealedLayout] = useState(null);

    useEffect(() => {
        setRevealedLayout(null);
        const timer = setTimeout(() => setRevealedLayout(layout), 2000);
        return () => clearTimeout(timer);
    }, [layout, position]);

    const isSidePanel = revealedLayout === 'sidePanel';
    const isOverlay = revealedLayout === 'overlay';
    const isHorizontal = revealedLayout === 'horizontalPanel';
    const panelSideClass = isSidePanel ? (position === 'Left' ? 'panel-left' : 'panel-right') : 'panel-none';
    const overlayPosClass = `ia-pp-${(isOverlay ? position : 'footer').toLowerCase()}`;

    return (
        <div className={`ia-video-frame ia-shoppable-frame ${isDisplay ? 'ia-format-display' : ''} ${isHorizontal ? 'ia-layout-horizontalPanel' : ''} ${panelSideClass}`}>
            <div className="ia-video-viewport">
                {isDisplay ? (
                    <img className="ia-ad-mock-media" src={shoppableAdBanner} alt="Shoppable display banner" />
                ) : (
                    <video className="ia-ad-mock-media" src={shoppableAdVideo} autoPlay loop muted playsInline />
                )}

                <div
                    className={`ia-product-panel-overlay ia-overlay-decoration ${overlayPosClass} ${isOverlay ? 'visible' : ''}`}
                >
                    <ProductStrip layout="overlay" panelStyle={panelStyle} ctaText={ctaText} />
                </div>
            </div>

            {/* Unlike Overlay (which floats over the video), Horizontal Panel reserves
                its own strip below the video — same non-overlapping approach as Side
                Panel, just shrinking height instead of width — so it's a sibling of
                the viewport rather than nested inside it. */}
            <div className={`ia-product-panel-horizontal ia-overlay-decoration ${isHorizontal ? 'visible' : ''}`}>
                <ProductStrip layout="horizontalPanel" panelStyle={panelStyle} ctaText={ctaText} />
            </div>

            <div className={`ia-product-panel-side ${isSidePanel ? 'visible' : ''}`}>
                <div className={`ia-product-panel-side-inner ${isSidePanel ? 'visible' : ''}`}>
                    <ProductStrip layout="sidePanel" panelStyle={panelStyle} ctaText={ctaText} />
                </div>
            </div>
        </div>
    );
};

export default ShoppableAdPreview;
export { ProductChip, PRODUCTS };
